import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { log } from '@/lib/logger';
import { Home, ArrowLeft, CheckCircle2, AlertTriangle, ExternalLink, Loader2, ShieldCheck, DollarSign, Clock, TrendingUp, CalendarClock } from 'lucide-react';

export default function PayoutsPage() {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [onboardingError, setOnboardingError] = useState('');
  const [forecast, setForecast] = useState(null);

  const fetchForecast = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/landlord/revenue-forecast`);
      setForecast(res.data);
    } catch (err) {
      log.debug('PayoutsPage: revenue-forecast fetch failed (non-fatal)', err);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stripe/connect/status`);
      setStatus(res.data);
    } catch (err) {
      toast.error('Failed to fetch payout status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'landlord') {
      navigate('/');
      return;
    }
    if (params.get('connect') === 'complete') {
      toast.success('Stripe onboarding submitted — verifying status...');
    } else if (params.get('connect') === 'refresh') {
      toast.info('Continuing your Stripe onboarding...');
    }
    fetchStatus();
    fetchForecast();
  }, [user, params, navigate, fetchStatus, fetchForecast]);

  const startOnboarding = async () => {
    setConnecting(true);
    setOnboardingError('');
    try {
      const res = await axios.post(
        `${API}/stripe/connect/account`,
        { return_url: `${window.location.origin}/landlord/payouts` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Redirect to Stripe hosted onboarding
      window.location.href = res.data.onboarding_url;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to start Stripe onboarding';
      setOnboardingError(detail);
      toast.error(detail, { duration: 8000 });
      setConnecting(false);
    }
  };

  const openStripeDashboard = async () => {
    try {
      const res = await axios.get(`${API}/stripe/connect/dashboard-link`, { headers: { Authorization: `Bearer ${token}` } });
      window.open(res.data.url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open Stripe dashboard');
    }
  };

  const isReady = status?.charges_enabled && status?.payouts_enabled;
  const hasAccount = status?.connected;
  const needsAction = hasAccount && !isReady;

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/landlord/dashboard')}>
            <Home className="w-7 h-7 text-primary" />
            <span className="text-2xl font-bold tracking-tight text-primary">PrimePocket</span>
          </div>
          <Button data-testid="back-to-dashboard-btn" variant="ghost" onClick={() => navigate('/landlord/dashboard')} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
        <div className="mb-10">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-primary">Payouts</h1>
          <p className="text-muted-foreground mt-2">Connect your bank account to receive automatic payouts from tenant payments.</p>
        </div>

        {forecast && forecast.active_lease_count > 0 && (
          <Card data-testid="revenue-forecast-widget" className="p-6 mb-8 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-primary">Revenue Forecast</h2>
                  <p className="text-xs text-muted-foreground">Rent expected in the next {forecast.horizon_days} days</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-primary tabular-nums" data-testid="forecast-total">
                  ${forecast.expected_next_30_days.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {forecast.active_lease_count} active {forecast.active_lease_count === 1 ? 'lease' : 'leases'}
                </div>
              </div>
            </div>

            {forecast.upcoming.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Upcoming rent due</div>
                <ul className="divide-y divide-border/50" data-testid="forecast-upcoming-list">
                  {forecast.upcoming.slice(0, 5).map((u) => (
                    <li key={u.lease_id} className="py-2 flex items-center justify-between gap-3" data-testid={`forecast-item-${u.lease_id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <CalendarClock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{u.tenant_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{u.property_title}</div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-semibold tabular-nums">${u.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        <div className="text-xs text-muted-foreground">
                          {u.days_until_due === 0 ? 'today' : u.days_until_due === 1 ? 'tomorrow' : `in ${u.days_until_due} days`}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {forecast.upcoming.length > 5 && (
                  <div className="text-xs text-muted-foreground text-center pt-2">
                    + {forecast.upcoming.length - 5} more upcoming
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-8 sm:py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : isReady ? (
          <Card className="p-8" data-testid="payouts-ready-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold">Payouts Active</h2>
                <p className="text-sm text-muted-foreground">Tenant payments transfer to your bank automatically.</p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 mb-6">
              <div className="p-4 rounded-lg bg-muted/40">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Charges</div>
                <div className="text-lg font-semibold flex items-center gap-2 mt-1">
                  <ShieldCheck className="w-4 h-4 text-green-600" /> Enabled
                </div>
              </div>
              <div className="p-4 rounded-lg bg-muted/40">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Payouts</div>
                <div className="text-lg font-semibold flex items-center gap-2 mt-1">
                  <DollarSign className="w-4 h-4 text-green-600" /> Enabled
                </div>
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button data-testid="open-stripe-dashboard-btn" onClick={openStripeDashboard} className="rounded-full bg-primary hover:bg-primary/90">
                <ExternalLink className="w-4 h-4 mr-2" /> Open Stripe Dashboard
              </Button>
              <Button data-testid="refresh-status-btn" variant="outline" onClick={fetchStatus} className="rounded-full">
                Refresh Status
              </Button>
            </div>
          </Card>
        ) : needsAction ? (
          <Card className="p-8" data-testid="payouts-action-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold">Complete Your Setup</h2>
                <p className="text-sm text-muted-foreground">Stripe needs a few more details before you can receive payouts.</p>
              </div>
            </div>
            {status.requirements_due?.length > 0 && (
              <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200">
                <div className="text-sm font-medium text-amber-900 mb-2">Pending items:</div>
                <ul className="text-sm text-amber-800 space-y-1">
                  {status.requirements_due.slice(0, 8).map((r) => (
                    <li key={r} className="flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5" /> {r.replace(/_/g, ' ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-3 flex-wrap">
              <Button data-testid="continue-onboarding-btn" onClick={startOnboarding} disabled={connecting} className="rounded-full bg-primary hover:bg-primary/90">
                {connecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...</> : <>Continue Onboarding <ExternalLink className="w-4 h-4 ml-2" /></>}
              </Button>
              <Button data-testid="refresh-status-btn" variant="outline" onClick={fetchStatus} className="rounded-full">
                I've completed the steps
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-8" data-testid="payouts-setup-card">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold">Set Up Automatic Payouts</h2>
                <p className="text-sm text-muted-foreground">Powered by Stripe. Get paid directly to your bank when tenants pay.</p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 mb-6">
              <div className="p-4 rounded-lg bg-muted/40">
                <div className="text-sm font-semibold">1. Verify identity</div>
                <div className="text-xs text-muted-foreground mt-1">Stripe collects your info securely.</div>
              </div>
              <div className="p-4 rounded-lg bg-muted/40">
                <div className="text-sm font-semibold">2. Add bank account</div>
                <div className="text-xs text-muted-foreground mt-1">Payouts land in 2 business days.</div>
              </div>
              <div className="p-4 rounded-lg bg-muted/40">
                <div className="text-sm font-semibold">3. Get paid</div>
                <div className="text-xs text-muted-foreground mt-1">Every tenant payment auto-splits.</div>
              </div>
            </div>

            <Button data-testid="start-onboarding-btn" onClick={startOnboarding} disabled={connecting} className="w-full sm:w-auto rounded-full bg-primary hover:bg-primary/90">
              {connecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe...</> : <>Connect with Stripe <ExternalLink className="w-4 h-4 ml-2" /></>}
            </Button>

            {onboardingError && (
              <div data-testid="connect-error-banner" className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-800 space-y-2">
                    <p className="font-medium">Couldn't start Stripe onboarding</p>
                    <p>{onboardingError}</p>
                    {/https?:\/\/dashboard\.stripe\.com\/connect/i.test(onboardingError) && (
                      <a
                        href="https://dashboard.stripe.com/connect"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-red-900 underline hover:no-underline"
                      >
                        Open Stripe Connect settings <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 p-4 rounded-lg bg-muted/30 text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Fee split:</p>
              Platform keeps 2% of down payments + 1% of application fees. The full remaining amount transfers directly to your Stripe balance.
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
